import { safeJSONParse } from '~/common/utils/safeJSONParse.util';

/**
 * A return address handed to `/user-verification` by a "return-required" KYC flow —
 * currently the Contest Vault withdraw link, which marks itself with `requireKyc=1`
 * (EDGE-31784). The user is sent to orb to complete mandatory KYC and must land back
 * where they came from on success so they can finish (e.g. withdraw).
 *
 * We persist the address here rather than relying on the `redirectTo` query param
 * because that param is dropped across the KYC round-trip — the `REFER` hop
 * (`/user-verification/refer`) carries no query, and the IDComply hosted form is a
 * full-page `window.location` redirect off-origin. localStorage survives both (and a
 * tab close), matching how the IDComply session token itself is persisted.
 *
 * Only honored when the user reaches an `ACCEPT` terminal; the deposit/welcome screen
 * remains the default for every other (non-return-required) KYC completion.
 */
export const KYC_RETURN_URL_KEY = 'kyc_return_url';

/**
 * Bounds staleness so an abandoned return address can't hijack a later, unrelated KYC
 * completion. Mirrors the IDComply session TTL.
 */
export const RETURN_URL_EXPIRY_MS = 30 * 60 * 1000;

// The persisted value is later fed to a full-page navigation, so an unvalidated URL
// would be an open redirect. Only same-parent-domain (`*.splashsports.com`) https
// targets — orb and the contests MFE share `.splashsports.com` — or safe same-origin
// relative paths may be honored.
const ALLOWED_RETURN_DOMAIN = 'splashsports.com';

// Any `.splashsports.com` origin works as the base for resolving relative paths — a
// relative target resolves to an allowlisted host, an absolute one keeps its own host.
const RELATIVE_RESOLUTION_ORIGIN = 'https://orb.splashsports.com';

// Backslashes and control chars (tab/newline/etc) must be rejected before parsing: the
// WHATWG URL parser rewrites `\`→`/` and strips tab/newline for http(s), so `/\evil.com`
// or `/<tab>/evil.com` would otherwise look "relative" here yet resolve off-origin at the
// navigation sink. Checked by code point to avoid control-char literals in source.
function hasUnsafeUrlChars(url: string): boolean {
  for (let i = 0; i < url.length; i += 1) {
    const code = url.charCodeAt(i);
    if (code < 0x20 || code === 0x7f || url[i] === '\\') {
      return true;
    }
  }
  return false;
}

export function isAllowedReturnUrl(url: unknown): boolean {
  // Fail closed on non-string input: a duplicated `?redirectTo=a&redirectTo=b` gives Next
  // a string[], and the pre-existing `as string` casts would otherwise flow it here and
  // throw on url.trim(). Empty / whitespace-only resolves to the sentinel origin root
  // (the parser trims it) — a meaningless target — so reject that too.
  if (typeof url !== 'string' || url.trim().length === 0) {
    return false;
  }
  if (hasUnsafeUrlChars(url)) {
    return false;
  }
  let hostname: string;
  let protocol: string;
  try {
    // Relative paths resolve against the sentinel (→ an allowlisted host); absolute URLs
    // ignore the base and keep their own origin. One uniform check for both shapes.
    ({ hostname, protocol } = new URL(url, RELATIVE_RESOLUTION_ORIGIN));
  } catch {
    return false;
  }
  if (protocol !== 'https:') {
    return false;
  }
  return hostname === ALLOWED_RETURN_DOMAIN || hostname.endsWith(`.${ALLOWED_RETURN_DOMAIN}`);
}

interface StoredReturnUrl {
  url: string;
  storedAt: number;
  withdrawalAmount?: number;
}

export type FreshReturn = Omit<StoredReturnUrl, 'storedAt'>;

function readWithdrawalAmount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function isStoredReturnUrl(value: unknown): value is StoredReturnUrl {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).url === 'string' &&
    typeof (value as Record<string, unknown>).storedAt === 'number'
  );
}

export function clearReturnUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.removeItem(KYC_RETURN_URL_KEY);
}

/**
 * The persisted return address, but only if it is fresh and still allowlisted;
 * otherwise clears it and returns null. Callers navigate to the value and then
 * `clearReturnUrl()` so it only ever represents the current attempt.
 */
export function getFreshReturn(): FreshReturn | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const parsed = safeJSONParse(localStorage.getItem(KYC_RETURN_URL_KEY));
  if (!isStoredReturnUrl(parsed)) {
    return null;
  }
  const isExpired = parsed.storedAt <= 0 || Date.now() - parsed.storedAt > RETURN_URL_EXPIRY_MS;
  if (isExpired || !isAllowedReturnUrl(parsed.url)) {
    clearReturnUrl();
    return null;
  }
  const withdrawalAmount = readWithdrawalAmount(parsed.withdrawalAmount);
  return withdrawalAmount === undefined
    ? { url: parsed.url }
    : { url: parsed.url, withdrawalAmount };
}

export function getFreshReturnUrl(): string | null {
  return getFreshReturn()?.url ?? null;
}

/** Persist a return address. No-ops on a value that fails the allowlist. */
export function storeReturnUrl(url: string, withdrawalAmount?: number): void {
  if (typeof window === 'undefined' || !isAllowedReturnUrl(url)) {
    return;
  }
  const existing = getFreshReturn();
  const amount =
    readWithdrawalAmount(withdrawalAmount) ??
    (existing?.url === url ? existing.withdrawalAmount : undefined);
  const record: StoredReturnUrl = { url, storedAt: Date.now() };
  if (amount !== undefined) {
    record.withdrawalAmount = amount;
  }
  localStorage.setItem(KYC_RETURN_URL_KEY, JSON.stringify(record));
}

/**
 * The routing decision for a KYC `ACCEPT` terminal, extracted as a pure function so the
 * branch is unit-testable: a preserved return address (a requireKyc=1 flow) wins over
 * the deposit/welcome interstitial, which stays the default otherwise.
 */
export type AcceptRedirect =
  | { readonly kind: 'return'; readonly url: string }
  | { readonly kind: 'deposit' }
  | { readonly kind: 'accept' };

export function resolveAcceptRedirect(
  returnUrl: string | null,
  isSkipWelcomeScreenEnabled: boolean
): AcceptRedirect {
  if (returnUrl) {
    return { kind: 'return', url: returnUrl };
  }
  return isSkipWelcomeScreenEnabled ? { kind: 'deposit' } : { kind: 'accept' };
}
